import React from 'react';
import { useTranslation } from 'react-i18next';

import { Geometry } from '../../../../../../viewer/elements/geometry';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { MoveIcon } from '../../../../skyview/right-menu/components/move-icon';
import { useSubscribeShowDeleteAssetDialog } from '../../../hooks/subscribe-show-delete-asset-dialog';
import { CategoryContext } from '../../../state/category-state';
import { AssetHeader } from '../../components/asset-blocks/asset-header';
import { ExportGeometryIcon } from '../../components/export-geometry-icon';
import { DeleteAssetDialog } from '../../dialogs/delete-asset-dialog';
import { DisplaySettingsDialog, DisplaySettingsIcon } from '../../dialogs/display-settings-dialog';
import { EditAssetNameDialog } from '../../dialogs/edit-asset-name-dialog';
import {
  PointListDialog,
  PointListIcon,
  useOpenPointListOnSelection,
} from '../../dialogs/point-list-dialog';
import { DeleteIcon } from '../../icons/delete-icon';
import { EditIcon } from '../../icons/edit-icon';

type Props = {
  asset: Geometry;
};

const AreaHeader = (props: Props) => {
  const { t } = useTranslation();
  const displaySettingsDialog = useDialog();
  const editNameDialog = useDialog();
  const deleteDialog = useDialog();
  const pointListDialog = useDialog();
  const { expandedAssetId, toggle: toggleAsset } = React.useContext(CategoryContext);
  const { isSharedView } = useSharedView();
  const assetLoadedAndVisible =
    props.asset.getStateVisibility() && props.asset.loadingStatus === 'loaded';
  useSubscribeShowDeleteAssetDialog(props.asset, deleteDialog, isSharedView);
  useOpenPointListOnSelection(props.asset, pointListDialog);

  // The points are listed in a shared view too, read-only.
  const pointListIcon = (
    <PointListIcon
      asset={props.asset}
      disabled={!assetLoadedAndVisible}
      key="pointList"
      onClick={(e) => {
        // The points are hovered and moved in the scene while the asset is selected.
        if (expandedAssetId !== props.asset.uuid) {
          toggleAsset(props.asset.uuid);
        }

        pointListDialog.show({ x: e.clientX, y: e.clientY });
      }}
    />
  );

  return (
    <>
      <AssetHeader
        additionalIcons={
          isSharedView
            ? [pointListIcon]
            : [
                pointListIcon,
                <DisplaySettingsIcon
                  disabled={!assetLoadedAndVisible}
                  key="displaySettings"
                  onClick={(e) => displaySettingsDialog.show()}
                />,
                <EditIcon
                  key="editIcon"
                  onClick={(e) => editNameDialog.show({ x: e.clientX, y: e.clientY })}
                />,
                <MoveIcon asset={props.asset} disabled={!assetLoadedAndVisible} key="moveIcon" />,
                <DeleteIcon
                  key="deleteIcon"
                  onClick={(e) => deleteDialog.show({ x: e.clientX, y: e.clientY })}
                />,
              ]
        }
        asset={props.asset}
        icons={[
          <ExportGeometryIcon
            asset={props.asset}
            key="exportIcon"
            title={t('area.icons.download', { ns: 'skyviewAssetMenu' })}
          />,
        ]}
        name={props.asset.name}
      />

      {editNameDialog.render((pos) => (
        <EditAssetNameDialog asset={props.asset} pos={pos} onClose={editNameDialog.hide} />
      ))}

      {deleteDialog.render((pos) => (
        <DeleteAssetDialog asset={props.asset} pos={pos} onClose={deleteDialog.hide} />
      ))}
      {displaySettingsDialog.render(
        <DisplaySettingsDialog asset={props.asset} dialog={displaySettingsDialog} />,
      )}
      {pointListDialog.render((pos) => (
        <PointListDialog asset={props.asset} dialog={pointListDialog} pos={pos} />
      ))}
    </>
  );
};

export { AreaHeader, Props as AreaHeaderProps };
